/**
 * TanStack Router's `Link` and `useNavigate`, over React Router.
 *
 * The kit uses exactly two things from its router — a `Link` that takes `to` with `$param`
 * placeholders and a `params` object, and a `useNavigate` called the same way. Rather than
 * adopt a second router for five call sites, this fills those two in: it substitutes the
 * placeholders and hands the result to React Router.
 */
import type { ReactNode } from 'react';
import { Link as RouterLink, useNavigate as useRouterNavigate } from 'react-router-dom';

/** `/work/$workItemKey` + `{ workItemKey: 'GOLD-1' }` becomes `/work/GOLD-1`. */
function fill(to: string, params?: Record<string, string | number>): string {
  if (!params) return to;
  return Object.entries(params).reduce(
    (path, [name, value]) => path.replace(`$${name}`, encodeURIComponent(String(value))),
    to,
  );
}

export function Link({
  to,
  params,
  children,
  className,
  title,
  onClick,
}: {
  to: string;
  params?: Record<string, string | number>;
  children: ReactNode;
  className?: string;
  title?: string;
  onClick?: () => void;
}) {
  return (
    <RouterLink to={fill(to, params)} className={className} title={title} onClick={onClick}>
      {children}
    </RouterLink>
  );
}

export function useNavigate() {
  const navigate = useRouterNavigate();
  /** Accepts the kit's `{ to, params }` shape and also a plain string. */
  return (options: string | { to: string; params?: Record<string, string | number>; replace?: boolean }) => {
    if (typeof options === 'string') return navigate(options);
    return navigate(fill(options.to, options.params), { replace: options.replace });
  };
}
